import { Link } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import { RestoreBonusSpPanel } from "@/components/superadmin/RestoreBonusSpPanel";
import AdminClosingCard from "@/components/admin/AdminClosingCard";
import { Image as ImageIcon, Package, LogOut, ArrowRight, Shield, Home as HomeIcon, RefreshCw, Loader2, Download, Upload, Calculator, FileText, Zap, Wrench } from "lucide-react";

/**
 * SuperadminDashboard — top-level control panel for the "superadmin" role.
 * Currently exposes two tools:
 *   1. Home Banners — upload / manage hero slider images
 *   2. Products     — reuses the existing admin product manager
 * Regular /admin routes are unchanged; superadmin can also access them
 * (ProtectedRoute treats superadmin as implicitly satisfying "admin").
 */
export default function SuperadminDashboard() {
  const { user, logout } = useAuth();
  const [rebuilding, setRebuilding] = useState(false);
  const [rebuildReport, setRebuildReport] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportSummary, setExportSummary] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importSummary, setImportSummary] = useState(null);
  const [simulating, setSimulating] = useState(false);
  const [simulateResult, setSimulateResult] = useState(null);
  const [injecting, setInjecting] = useState(false);
  const [injectSummary, setInjectSummary] = useState(null);
  const [repairing, setRepairing] = useState(false);
  const [repairSummary, setRepairSummary] = useState(null);
  const [cleaningRules, setCleaningRules] = useState(false);
  const [cleanupSummary, setCleanupSummary] = useState(null);
  const [recomputing, setRecomputing] = useState(false);
  const [recomputeSummary, setRecomputeSummary] = useState(null);
  const [syncingWallets, setSyncingWallets] = useState(false);
  const [syncWalletsSummary, setSyncWalletsSummary] = useState(null);
  const [fixingDescs, setFixingDescs] = useState(false);
  const [fixDescsSummary, setFixDescsSummary] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [commitSummary, setCommitSummary] = useState(null);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [csvSummary, setCsvSummary] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const [reconcileSummary, setReconcileSummary] = useState(null);
  const [reversingStartup, setReversingStartup] = useState(false);
  const [reverseStartupSummary, setReverseStartupSummary] = useState(null);
  const [committingClosing, setCommittingClosing] = useState(false);
  const [commitClosingSummary, setCommitClosingSummary] = useState(null);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [markPaidSummary, setMarkPaidSummary] = useState(null);
  const [amending, setAmending] = useState(false);
  const [amendSummary, setAmendSummary] = useState(null);
  const [finalizing, setFinalizing] = useState(false);
  const [finalizeSummary, setFinalizeSummary] = useState(null);
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [finalizePayload, setFinalizePayload] = useState({
    period_label: "26_sep_to_03_oct_2026",
    source: "finalize_cycle_8_5l",
    reconciliations_json: "[]",
    reversals_json: "[]",
    mark_paid: false,
    paid_at: "",
    lock_after_commit: true,
    note: "",
  });

  // v37 round 2 PM rev 8.5f — monthly bonus reconciliation (no-scalar-$inc mode)
  const recordMonthlyBonusReconciliation = async () => {
    const user_code = window.prompt("User code (e.g. SM856487)")?.trim();
    if (!user_code) return;
    const tier = parseInt(window.prompt("Tier (1-13)") || "0", 10);
    if (!tier || tier < 1 || tier > 13) { toast.error("Invalid tier"); return; }
    const period = window.prompt("Period (YYYY-MM, e.g. 2026-09)")?.trim();
    if (!period || !/^\d{4}-\d{2}$/.test(period)) { toast.error("Invalid period"); return; }
    const consumed_left_sp = parseInt(window.prompt("Consumed left SP") || "0", 10);
    const consumed_right_sp = parseInt(window.prompt("Consumed right SP") || "0", 10);
    const reason = window.prompt("Reason (operator-authorised evidence note)")?.trim();
    if (!reason) { toast.error("Reason required"); return; }
    const credit_scalar = window.confirm("Credit scalar wallet too? Click OK only if scalar does NOT already carry this ₹. Cancel = ledger-only (recommended for drift reconciliation).");
    if (!window.confirm(`Confirm: record Tier-${tier} ₹? bonus for ${user_code} period ${period}, consume ${consumed_left_sp}L/${consumed_right_sp}R SPs, credit_scalar=${credit_scalar}?`)) return;
    setReconciling(true);
    setReconcileSummary(null);
    try {
      const { data } = await api.post("/superadmin/record-monthly-bonus-reconciliation", {
        user_code, tier, period, consumed_left_sp, consumed_right_sp, reason, credit_scalar,
      });
      setReconcileSummary(data);
      toast.success(`Reconciled ₹${data.bonus_rs} Tier-${data.tier} for ${data.user_code} (${data.period})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reconciliation failed");
    } finally {
      setReconciling(false);
    }
  };

  // v37 rev 8.5i — reverse a soft startup over-credit (Meenu ₹600 case)
  const reverseStartupOvercredit = async () => {
    const user_code = window.prompt("User code (e.g. SM521868)")?.trim();
    if (!user_code) return;
    const amtRaw = window.prompt("Amount ₹ to debit (e.g. 600)")?.trim();
    const amount_rs = parseFloat(amtRaw || "0");
    if (!amount_rs || amount_rs <= 0) { toast.error("Invalid amount"); return; }
    const reason = window.prompt(
      "Reason (operator-authorised audit note, ≥10 chars)",
      "Startup slab reversal — 20:20 + 30:30 superseded by 50:50 pair match per operator rule 2026-10-03."
    )?.trim();
    if (!reason || reason.length < 10) { toast.error("Reason must be ≥ 10 characters"); return; }
    if (!window.confirm(`DEBIT ₹${amount_rs} from ${user_code}'s wallet.income? This is IDEMPOTENT per day (safe re-click returns 409).`)) return;
    setReversingStartup(true);
    setReverseStartupSummary(null);
    try {
      const { data } = await api.post("/superadmin/reverse-startup-overcredit", {
        user_code, amount_rs, reason,
      });
      setReverseStartupSummary(data);
      toast.success(`Debited ₹${data.amount_rs} from ${data.user_code} (scalar ${data.scalar_before_rs} → ${data.scalar_after_rs})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reversal failed");
    } finally {
      setReversingStartup(false);
    }
  };

  // v37 rev 8.5i — commit current closing cycle into weekly_closings_ledger
  const commitCurrentClosing = async () => {
    const period_label = window.prompt(
      "Period label (e.g. 26_sep_to_03_oct_2026)",
      "26_sep_to_03_oct_2026",
    )?.trim();
    if (!period_label) return;
    const source = window.prompt("Source string (uniqueness key for idempotency)", "manual_commit_8_5i")?.trim();
    if (!source) return;
    const run_dry_first = window.confirm("Run DRY-RUN first? OK = dry-run (no writes). Cancel = commit directly.");
    setCommittingClosing(true);
    setCommitClosingSummary(null);
    try {
      // Dry-run always first if user opted in
      if (run_dry_first) {
        const { data: dry } = await api.post("/superadmin/commit-current-closing", {
          period_label, source, dry_run: true,
        });
        setCommitClosingSummary(dry);
        toast.success(`DRY-RUN · ${dry.would_insert_rows} rows · ₹${dry.total_net_rs} net · ${dry.on_hold_count || 0} on hold`);
        const holdList = (dry.on_hold || []).map((h) => `  ${h.user_code} ${h.name} — Self SP ${h.self_sp} (needs ${h.self_sp_needed}) · ₹${h.net_if_paid_rs} net kept`).join("\n");
        const proceed = window.confirm(
          `DRY-RUN complete. ${dry.would_insert_rows} rows would be inserted (₹${dry.total_gross_rs} gross · ₹${dry.total_net_rs} net).\n\n` +
          (holdList ? `ON HOLD (Self SP below 50, not paid, income kept):\n${holdList}\n\n` : "") +
          `Proceed with the ACTUAL commit now?`
        );
        if (!proceed) {
          toast.info("Commit aborted — only dry-run was executed");
          return;
        }
      } else {
        if (!window.confirm(`Commit ${period_label} directly WITHOUT dry-run? This writes to the ledger.`)) return;
      }
      const { data } = await api.post("/superadmin/commit-current-closing", {
        period_label, source,
      });
      setCommitClosingSummary(data);
      toast.success(`Committed ${data.inserted_rows} ledger rows · ₹${data.total_net_rs} net · status=open`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Commit failed");
    } finally {
      setCommittingClosing(false);
    }
  };

  // v37 rev 8.5i — mark a closing period as paid (flips status=paid + stamps paid_at)
  const markClosingPaid = async () => {
    const period_label = window.prompt(
      "Week to mark PAID (e.g. 03_oct_to_10_oct_2026)",
      "",
    )?.trim();
    if (!period_label) return;
    const paid_at_default = new Date().toISOString();
    const paid_at = window.prompt("paid_at (ISO timestamp)", paid_at_default)?.trim();
    if (!paid_at) return;
    const closed_at = window.prompt("closed_at — leave EMPTY to keep the saved closing time", "")?.trim() || "";
    if (!window.confirm(`Mark period "${period_label}" as PAID at ${paid_at}? This flips every ledger row's status to 'paid'.`)) return;
    setMarkingPaid(true);
    setMarkPaidSummary(null);
    try {
      const body = { period_label, paid_at };
      if (closed_at) body.closed_at = closed_at;
      const { data } = await api.post("/superadmin/mark-closing-paid", body);
      setMarkPaidSummary(data);
      toast.success(`Marked PAID · matched ${data.matched_rows} · modified ${data.modified_rows}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mark-paid failed");
    } finally {
      setMarkingPaid(false);
    }
  };

  // v37 rev 8.5j — amend an OPEN weekly_closings_ledger row (post-commit reconciliation)
  const amendOpenClosingRow = async () => {
    const period_label = window.prompt("Period label", "26_sep_to_03_oct_2026")?.trim();
    if (!period_label) return;
    const user_code = window.prompt("User code to amend (e.g. SM856487)")?.trim();
    if (!user_code) return;
    const source = window.prompt("Source filter (optional, blank to auto-detect)", "manual_commit_8_5i")?.trim() || "";
    const pair = window.prompt("New pair_income_rs (blank = keep existing)");
    const dref = window.prompt("New direct_ref_rs (blank = keep)");
    const bonus = window.prompt("New monthly_bonus_rs (blank = keep)");
    const rank = window.prompt("New rank_reward_rs (blank = keep)");
    const reason = window.prompt("Reason (≥10 chars)")?.trim();
    if (!reason || reason.length < 10) { toast.error("Reason too short"); return; }
    const body = { period_label, user_code, reason };
    if (source) body.source = source;
    if (pair !== null && pair !== "") body.pair_income_rs = parseFloat(pair);
    if (dref !== null && dref !== "") body.direct_ref_rs = parseFloat(dref);
    if (bonus !== null && bonus !== "") body.monthly_bonus_rs = parseFloat(bonus);
    if (rank !== null && rank !== "") body.rank_reward_rs = parseFloat(rank);
    if (!window.confirm(`Amend open closing row for ${user_code} / ${period_label}?\n${JSON.stringify(body, null, 2)}`)) return;
    setAmending(true);
    setAmendSummary(null);
    try {
      const { data } = await api.post("/superadmin/amend-open-closing-row", body);
      setAmendSummary(data);
      toast.success(`Amended ${data.user_code} · gross ${data.before.gross_rs} → ${data.after.gross_rs} · net ${data.before.net_rs} → ${data.after.net_rs}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Amend failed");
    } finally {
      setAmending(false);
    }
  };

  // v37 rev 8.5l — atomic finalize-closing-cycle (reconcile → reverse → commit)
  const runFinalizeCycle = async (dry_run) => {
    let reconciliations = [];
    let reversals = [];
    try {
      reconciliations = JSON.parse(finalizePayload.reconciliations_json || "[]");
    } catch {
      toast.error("Reconciliations JSON is invalid");
      return;
    }
    try {
      reversals = JSON.parse(finalizePayload.reversals_json || "[]");
    } catch {
      toast.error("Reversals JSON is invalid");
      return;
    }
    if (!Array.isArray(reconciliations) || !Array.isArray(reversals)) {
      toast.error("reconciliations and reversals must be arrays");
      return;
    }
    const body = {
      period_label: finalizePayload.period_label.trim(),
      source: finalizePayload.source.trim(),
      reconciliations,
      reversals,
      mark_paid: !!finalizePayload.mark_paid,
      paid_at: finalizePayload.paid_at.trim() || null,
      lock_after_commit: !!finalizePayload.lock_after_commit,
      dry_run,
      note: finalizePayload.note.trim(),
    };
    if (!body.period_label) {
      toast.error("period_label is required");
      return;
    }
    if (!dry_run) {
      const confirmMsg =
        `COMMIT finalize-cycle for ${body.period_label} (source=${body.source})?\n\n` +
        `• Reconciliations: ${reconciliations.length}\n` +
        `• Reversals: ${reversals.length}\n` +
        `• Mark Paid: ${body.mark_paid ? body.paid_at : "no"}\n` +
        `• Lock after commit: ${body.lock_after_commit ? "yes" : "no"}\n\n` +
        `This is NOT dry-run. Proceed?`;
      if (!window.confirm(confirmMsg)) return;
    }
    setFinalizing(true);
    setFinalizeSummary(null);
    try {
      const { data } = await api.post("/superadmin/finalize-closing-cycle", body);
      setFinalizeSummary(data);
      const t = data?.totals || {};
      toast.success(
        `${dry_run ? "DRY-RUN" : "COMMITTED"} · ${t.would_insert_rows || 0} rows · ₹${t.total_net_rs || 0} net`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Finalize failed");
    } finally {
      setFinalizing(false);
    }
  };

  const exportClosingSnapshotCsv = async () => {
    setExportingCsv(true);
    setCsvSummary(null);
    try {
      const res = await api.get("/superadmin/export-closing-snapshot-csv", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const cd = res.headers?.["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/.exec(cd);
      const stamp = new Date().toISOString().replace(/[:T]/g, "-").slice(0, 16);
      const filename = match ? match[1] : `closing_snapshot_${stamp}.csv`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      // Count rows for the summary tile
      const text = await blob.text();
      const total = Math.max(0, text.split("\n").filter(Boolean).length - 1);
      const payable = (text.match(/,YES\s*$/gm) || []).length;
      setCsvSummary({ total, payable, filename, downloadedAt: new Date().toLocaleString() });
      toast.success(`Downloaded ${total} rows · ${payable} payable in current closing`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "CSV export failed");
    } finally {
      setExportingCsv(false);
    }
  };

  const exportFullState = async () => {
    setExporting(true);
    setExportSummary(null);
    try {
      const { data } = await api.get("/superadmin/export/full-state");
      // Trigger a browser download so the file lands on the admin's machine
      const stamp = new Date().toISOString().slice(0, 10);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `samara_full_state_${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      const total = Object.values(data.counts || {}).reduce((a, b) => a + b, 0);
      setExportSummary({ counts: data.counts, total, downloadedAt: new Date().toLocaleString() });
      toast.success(`Downloaded ${total.toLocaleString()} records across ${Object.keys(data.counts).length} collections`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const importFullState = async (file) => {
    if (!file) return;
    const proceed = window.confirm(
      `⚠️ DESTRUCTIVE — this will WIPE every mirrored collection in THIS environment ` +
      `and replace it with the contents of ${file.name}.\n\n` +
      `Only run this on the PREVIEW / UAT environment.\n\nProceed?`
    );
    if (!proceed) return;
    setImporting(true);
    setImportSummary(null);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const body = { confirm: "WIPE_PREVIEW", data: parsed.data || parsed };
      const { data } = await api.post(
        "/superadmin/import/full-state?confirm=WIPE_PREVIEW",
        body,
        { headers: { "Content-Type": "application/json" } }
      );
      const totalInserted = Object.values(data.inserted || {}).reduce((a, b) => a + b, 0);
      setImportSummary({
        wiped: data.wiped,
        inserted: data.inserted,
        totalInserted,
        importedAt: new Date().toLocaleString(),
      });
      toast.success(`Imported ${totalInserted.toLocaleString()} records`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Import failed: ${e.message}`);
    } finally {
      setImporting(false);
    }
  };

  const runClosingSimulate = async () => {
    setSimulating(true);
    setSimulateResult(null);
    try {
      const { data } = await api.get("/superadmin/preview/closing-simulate");
      setSimulateResult(data);
      toast.success(
        `Closing simulated — ₹${(data.grand_totals?.projected_grand_total_rs || 0).toLocaleString("en-IN")} across ${data.grand_totals?.active_users || 0} active IBAs`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Simulate failed");
    } finally {
      setSimulating(false);
    }
  };

  const downloadClosingCsv = async (activeOnly = false) => {
    try {
      const url_ = activeOnly
        ? "/superadmin/preview/closing-simulate?format=csv&active_only=true"
        : "/superadmin/preview/closing-simulate?format=csv";
      const res = await api.get(url_, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = activeOnly
        ? `samara_active_weekly_payout_${new Date().toISOString().slice(0, 10)}.csv`
        : `samara_closing_simulate_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error("CSV export failed");
    }
  };

  const injectSpPurchases = async (file) => {
    if (!file) return;
    let body;
    try {
      body = JSON.parse(await file.text());
    } catch (err) {
      toast.error("File is not valid JSON");
      return;
    }
    const purchases = Array.isArray(body?.purchases) ? body.purchases : null;
    if (!purchases?.length) {
      toast.error("JSON must have a 'purchases' array with {user_code, sp}");
      return;
    }
    const totalSp = purchases.reduce((s, p) => s + Number(p.sp || 0), 0);
    const proceed = window.confirm(
      `Inject ${purchases.length} SP purchases (total ${totalSp} SP) into THIS environment?\n\n` +
      `This will:\n` +
      `  • Activate each IBA that crosses 50 SP\n` +
      `  • Credit self-retail, booster, startup slabs instantly\n` +
      `  • Propagate SP up the tree (triggers pair matching + direct-referral)\n\n` +
      `First 3 entries:\n` +
      purchases.slice(0, 3).map((p) => `  ${p.user_code} → ${p.sp} SP`).join("\n") +
      (purchases.length > 3 ? `\n  … +${purchases.length - 3} more` : "") +
      `\n\nProceed?`
    );
    if (!proceed) return;
    setInjecting(true);
    setInjectSummary(null);
    try {
      const { data } = await api.post("/superadmin/preview/inject-sp", body);
      setInjectSummary({
        successful: data.successful,
        total: data.total_purchases,
        results: data.results,
        injectedAt: new Date().toLocaleString(),
      });
      toast.success(`Injected ${data.successful}/${data.total_purchases} SP purchases`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Inject failed: ${e.message}`);
    } finally {
      setInjecting(false);
    }
  };

  const runTreeRepair = async () => {
    if (!window.confirm(
      "Rebuild every tree pointer + fix parent_code drift for mis-placements?\n\n" +
      "Safe & idempotent. Run this once on prod after deploying the tree-repair fix."
    )) return;
    setRepairing(true);
    setRepairSummary(null);
    try {
      const { data } = await api.post("/admin/tree/repair");
      setRepairSummary(data);
      toast.success(
        `Tree repaired — ${data.parent_code_fixes} parent_code fix${data.parent_code_fixes === 1 ? "" : "es"}, ${data.repaired_pointers} pointers rebuilt`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Repair failed");
    } finally {
      setRepairing(false);
    }
  };

  const applyNewRulesCleanup = async () => {
    if (!window.confirm(
      "Apply the two new commission rules to THIS environment?\n\n" +
      "  1. Delete ALL self_retail incentives (IBA does not earn ₹500 on own package).\n" +
      "  2. Delete single-leg booster credits (booster now needs BOTH legs ≥ 50 SP).\n" +
      "  3. Delete every income credited to admin / superadmin accounts.\n\n" +
      "Deducts each user's wallet.income by the removed amount and resets booster/self-retail flags. Safe & idempotent.\n\nProceed?"
    )) return;
    setCleaningRules(true);
    setCleanupSummary(null);
    try {
      const { data } = await api.post(
        "/superadmin/preview/apply-new-rules-cleanup?confirm=APPLY_NEW_RULES"
      );
      setCleanupSummary(data);
      toast.success(
        `Rules applied — ${data.incomes_deleted} incomes removed · ₹${(data.wallet_debits_total_rs || 0).toLocaleString("en-IN")} reversed`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cleanup failed");
    } finally {
      setCleaningRules(false);
    }
  };

  const recomputeWallets = async () => {
    if (!window.confirm(
      "Recompute every user's wallet.income from the incomes ledger?\n\n" +
      "wallet.income = SUM(incomes) − SUM(real order/payout debits).\n" +
      "Also normalises any wallet_txns whose direction was left null.\n" +
      "Safe & idempotent — clamps negative balances to ₹0.\n\nProceed?"
    )) return;
    setRecomputing(true);
    setRecomputeSummary(null);
    try {
      const { data } = await api.post(
        "/superadmin/preview/recompute-wallets?confirm=RECOMPUTE"
      );
      setRecomputeSummary(data);
      toast.success(
        `Wallets recomputed — ${data.users_adjusted} adjusted · ${data.wallet_txns_direction_healed} txns healed`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recompute failed");
    } finally {
      setRecomputing(false);
    }
  };

  const syncWalletsToPayout = async () => {
    if (!window.confirm(
      "Set every IBA's wallet.income to their PROJECTED WEEKLY PAYOUT?\n\n" +
      "Formula:\n" +
      "  wallet.income = pair_income + direct_ref + monthly_bonus + rank_reward\n\n" +
      "Startup / booster / self_retail rows stay in the incomes ledger (audit trail)\n" +
      "but are EXCLUDED from wallet.income under the customer's weekly-payout rule.\n\n" +
      "Verified numbers: Javed ₹9,500 · Vinod ₹8,700 · Sunil ₹500 · every row matches Simulate.\n\nProceed?"
    )) return;
    setSyncingWallets(true);
    setSyncWalletsSummary(null);
    try {
      const { data } = await api.post(
        "/superadmin/preview/sync-wallets-to-payout?confirm=SYNC_WALLETS"
      );
      setSyncWalletsSummary(data);
      toast.success(
        `Wallets synced — ${data.users_synced} synced · total wallet = ₹${Number(data.total_wallet_income_rs || 0).toLocaleString("en-IN")}`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncingWallets(false);
    }
  };

  const fixTransactionDescriptions = async () => {
    if (!window.confirm(
      "Rewrite old transaction wording in incomes + wallet_txns?\n\n" +
      "• Booster: '(no matching required)' → '(both legs qualified)'\n" +
      "• Direct Referral EXPIRED: 'seq #N EXPIRED — sponsor not yet activated'\n" +
      "  → 'Direct Referral seq #N — did not qualify (30-day window · sponsor was not active on joining)'\n\n" +
      "Idempotent · safe to re-run · no amount change.\n\nProceed?"
    )) return;
    setFixingDescs(true);
    setFixDescsSummary(null);
    try {
      const { data } = await api.post(
        "/superadmin/preview/fix-transaction-descriptions?confirm=FIX_DESCRIPTIONS"
      );
      setFixDescsSummary(data);
      toast.success(
        `Descriptions rewritten — ${data.total_rows_rewritten} rows across incomes + wallet_txns`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fix failed");
    } finally {
      setFixingDescs(false);
    }
  };

  const commitWeeklyClosing = async () => {
    const period = window.prompt(
      "Commit weekly closing — enter the period label (e.g. 05_sep_to_12_sep_2026).\n\n" +
      "Books current projected payout as REAL incomes + wallet_txns (paid_out=true),\n" +
      "decrements SP legs by matched, decrements monthly SP by bonus tier consumption,\n" +
      "and zeros wallet.income. Idempotent per period_label. IRREVERSIBLE.",
      "05_sep_to_12_sep_2026",
    );
    if (!period) return;
    if (!window.confirm(
      `Commit closing for period "${period}"?\n\n` +
      "This will:\n" +
      " • Insert ledger rows for pair_matching + monthly_bonus + rank_reward\n" +
      " • Debit each IBA's wallet.income to ₹0 (payout to bank recorded)\n" +
      " • Decrement left_sp/right_sp by matched SP (carry-forward)\n" +
      " • Decrement monthly_left/right_sp by bonus tier consumption\n\n" +
      "IRREVERSIBLE. Refuses to re-run for the same period. Proceed?"
    )) return;
    setCommitting(true);
    setCommitSummary(null);
    try {
      const { data } = await api.post(
        `/superadmin/preview/commit-weekly-closing?period_label=${encodeURIComponent(period)}&confirm=COMMIT`
      );
      setCommitSummary(data);
      toast.success(
        `Closing committed — ${data.users_committed} IBAs · gross ₹${Number(data.total_gross_rs || 0).toLocaleString("en-IN")}`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Commit failed");
    } finally {
      setCommitting(false);
    }
  };

  const rebuildCatalogue = async () => {
    if (!window.confirm(
      "Force-rewrite the 33-SKU catalogue to the canonical spec?\n\n" +
      "Use this if the current environment (usually production) has missing " +
      "SKUs, wrong images or wrong names. Existing admin-uploaded images are " +
      "preserved when the file exists on disk. This is safe & idempotent."
    )) return;
    setRebuilding(true);
    setRebuildReport(null);
    try {
      const { data } = await api.post("/superadmin/rebuild-catalogue");
      setRebuildReport(data);
      toast.success(`Catalogue rebuilt — inserted ${data.inserted_count}, updated ${data.updated_count}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rebuild failed");
    } finally {
      setRebuilding(false);
    }
  };

  const tools = [
    {
      to: "/superadmin/banners",
      testid: "sa-tool-banners",
      icon: ImageIcon,
      title: "Home Banners",
      subtitle: "Upload, reorder & manage hero slider images shown on the public home page.",
      cta: "Manage Banners",
      accent: "from-gold to-gold-deep",
    },
    {
      to: "/admin/products",
      testid: "sa-tool-products",
      icon: Package,
      title: "Products",
      subtitle: "Upload product photos, edit prices, categories & stock. Same catalogue reflects on public site, IBA shop & Franchise POS.",
      cta: "Manage Products",
      accent: "from-royal to-royal-deep",
    },
  ];

  return (
    <div className="min-h-screen bg-ivory">
      {/* Slim top-bar */}
      <header className="bg-royal text-white border-b border-gold/30">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 grid place-items-center bg-gold/20 border border-gold/40 rounded-sm">
              <Shield size={16} className="text-gold" />
            </div>
            <div>
              <div className="text-[9px] uppercase tracking-[0.35em] text-gold/80">Superadmin</div>
              <div className="font-serif text-sm">{user?.name || "Super Admin"} · {user?.user_code}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              data-testid="sa-nav-home"
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-white/80 hover:text-gold border border-white/25 hover:border-gold rounded-sm"
            >
              <HomeIcon size={12} /> Website
            </Link>
            <button
              onClick={logout}
              data-testid="sa-logout"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-white/80 hover:text-rose border border-white/25 hover:border-rose rounded-sm"
            >
              <LogOut size={12} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="mb-10">
          <div className="text-[10px] uppercase tracking-[0.4em] text-gold-deep mb-2">Control Panel</div>
          <h1 className="font-serif text-4xl text-royal">Manage the public experience.</h1>
          <p className="mt-2 text-sm text-navy/60 max-w-2xl">
            Two lightweight tools — banners for the home slider and product uploads that reflect
            everywhere at once. Regular admin views are unaffected.
          </p>
        </div>

        <div className="mb-10" data-testid="sa-weekly-closing">
          <AdminClosingCard />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {tools.map((t) => {
            const Icon = t.icon;
            return (
              <Link
                to={t.to}
                key={t.to}
                data-testid={t.testid}
                className="group relative bg-white border border-line rounded-sm overflow-hidden hover:border-gold/60 hover:shadow-[0_16px_40px_-10px_rgba(11,61,145,0.20)] transition-all"
              >
                <div className={`h-1.5 bg-gradient-to-r ${t.accent}`} />
                <div className="p-7">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 grid place-items-center bg-gradient-to-br ${t.accent} text-white rounded-sm shadow-sm shrink-0`}>
                      <Icon size={20} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h2 className="font-serif text-2xl text-royal leading-tight">{t.title}</h2>
                      <p className="mt-2 text-sm text-navy/60 leading-relaxed">{t.subtitle}</p>
                    </div>
                  </div>
                  <div className="mt-6 pt-4 border-t border-dashed border-line flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-[0.3em] text-gold-deep">{t.cta}</span>
                    <ArrowRight size={16} className="text-gold group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        <div className="mt-12 pt-8 border-t border-dashed border-line">
          {/* v37 rev 8.5l · Finalize Weekly Closing — PRIMARY (atomic) */}
          <div className="bg-emerald-50 border-2 border-emerald-300 rounded-sm p-5 mb-6" data-testid="sa-finalize-cycle-panel">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 grid place-items-center bg-emerald-100 border border-emerald-300 rounded-sm shrink-0">
                <Zap size={16} className="text-emerald-700" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-serif text-lg text-royal">Finalize Weekly Closing</div>
                  <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-emerald-600 text-white font-bold">Primary · Atomic</span>
                </div>
                <p className="text-xs text-navy/60 mt-1 leading-relaxed">
                  Runs <strong>reconciliations → reversals → commit</strong> in strict order (one orchestrator call).
                  Pre-flight dry-run shows the full plan before any writes. Prevents the "stale frozen row" bug caused
                  by running these steps separately. See <code>ARCHITECTURE_SAME_TABLE_LESSON.md</code>.
                </p>
                {!finalizeOpen ? (
                  <button
                    onClick={() => setFinalizeOpen(true)}
                    data-testid="sa-finalize-open"
                    className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-bold rounded-sm"
                  >
                    <Zap size={12} /> Open Finalize Panel
                  </button>
                ) : (
                  <div className="mt-4 bg-white border border-emerald-300 rounded-sm p-4 space-y-3" data-testid="sa-finalize-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <label className="text-[10px] uppercase tracking-widest text-navy/70 font-bold">
                        Period label
                        <input
                          data-testid="sa-finalize-period"
                          className="mt-1 w-full font-mono text-xs border border-navy/20 rounded-sm px-2 py-1.5"
                          value={finalizePayload.period_label}
                          onChange={(e) => setFinalizePayload((p) => ({ ...p, period_label: e.target.value }))}
                        />
                      </label>
                      <label className="text-[10px] uppercase tracking-widest text-navy/70 font-bold">
                        Source (cycle idempotency key)
                        <input
                          data-testid="sa-finalize-source"
                          className="mt-1 w-full font-mono text-xs border border-navy/20 rounded-sm px-2 py-1.5"
                          value={finalizePayload.source}
                          onChange={(e) => setFinalizePayload((p) => ({ ...p, source: e.target.value }))}
                        />
                      </label>
                    </div>
                    <label className="block text-[10px] uppercase tracking-widest text-navy/70 font-bold">
                      Reconciliations (JSON array of monthly-bonus records)
                      <textarea
                        data-testid="sa-finalize-reconciliations"
                        className="mt-1 w-full font-mono text-[11px] border border-navy/20 rounded-sm px-2 py-1.5 h-20"
                        placeholder='[{"user_code":"SM856487","tier":1,"period":"2026-09","consumed_left_sp":0,"consumed_right_sp":0,"reason":"Sept Tier-1 bonus reconciliation","credit_scalar":false}]'
                        value={finalizePayload.reconciliations_json}
                        onChange={(e) => setFinalizePayload((p) => ({ ...p, reconciliations_json: e.target.value }))}
                      />
                    </label>
                    <label className="block text-[10px] uppercase tracking-widest text-navy/70 font-bold">
                      Reversals (JSON array of startup over-credit debits)
                      <textarea
                        data-testid="sa-finalize-reversals"
                        className="mt-1 w-full font-mono text-[11px] border border-navy/20 rounded-sm px-2 py-1.5 h-20"
                        placeholder='[{"user_code":"SM521868","amount_rs":600,"reason":"Startup slab reversal post 50:50 pair match"}]'
                        value={finalizePayload.reversals_json}
                        onChange={(e) => setFinalizePayload((p) => ({ ...p, reversals_json: e.target.value }))}
                      />
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <label className="text-[10px] uppercase tracking-widest text-navy/70 font-bold flex items-center gap-2">
                        <input
                          type="checkbox"
                          data-testid="sa-finalize-mark-paid"
                          checked={finalizePayload.mark_paid}
                          onChange={(e) => setFinalizePayload((p) => ({ ...p, mark_paid: e.target.checked }))}
                        />
                        Mark paid after commit
                      </label>
                      <label className="text-[10px] uppercase tracking-widest text-navy/70 font-bold">
                        paid_at (ISO)
                        <input
                          data-testid="sa-finalize-paid-at"
                          className="mt-1 w-full font-mono text-xs border border-navy/20 rounded-sm px-2 py-1.5"
                          placeholder="2026-10-04T10:30:00+05:30"
                          value={finalizePayload.paid_at}
                          onChange={(e) => setFinalizePayload((p) => ({ ...p, paid_at: e.target.value }))}
                        />
                      </label>
                      <label className="text-[10px] uppercase tracking-widest text-navy/70 font-bold flex items-center gap-2">
                        <input
                          type="checkbox"
                          data-testid="sa-finalize-lock"
                          checked={finalizePayload.lock_after_commit}
                          onChange={(e) => setFinalizePayload((p) => ({ ...p, lock_after_commit: e.target.checked }))}
                        />
                        Lock after commit
                      </label>
                    </div>
                    <div className="flex items-center gap-2 pt-2 border-t border-emerald-200">
                      <button
                        onClick={() => runFinalizeCycle(true)}
                        disabled={finalizing}
                        data-testid="sa-finalize-dry-run"
                        className="inline-flex items-center gap-2 px-3 py-2 border border-emerald-700 text-emerald-800 hover:bg-emerald-50 text-xs font-bold rounded-sm disabled:opacity-50"
                      >
                        {finalizing ? <Loader2 size={12} className="animate-spin" /> : <Calculator size={12} />}
                        Dry-run (plan only)
                      </button>
                      <button
                        onClick={() => runFinalizeCycle(false)}
                        disabled={finalizing}
                        data-testid="sa-finalize-commit"
                        className="inline-flex items-center gap-2 px-3 py-2 bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-bold rounded-sm disabled:opacity-50"
                      >
                        {finalizing ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />}
                        Commit (apply)
                      </button>
                      <button
                        onClick={() => { setFinalizeOpen(false); setFinalizeSummary(null); }}
                        data-testid="sa-finalize-close"
                        className="ml-auto inline-flex items-center gap-2 px-3 py-2 text-xs text-navy/60 hover:text-navy border border-navy/20 rounded-sm"
                      >
                        Close
                      </button>
                    </div>
                    {finalizeSummary ? (
                      <div className="mt-2 text-[11px] font-mono bg-emerald-50/60 border border-emerald-200 rounded-sm p-3 text-navy/80" data-testid="sa-finalize-summary">
                        <div className="font-semibold text-emerald-800">
                          {finalizeSummary.dry_run ? "DRY-RUN" : "COMMITTED"} · {finalizeSummary.period_label} · source={finalizeSummary.source}
                        </div>
                        <div className="mt-1 grid grid-cols-3 gap-x-4 gap-y-0.5">
                          <div><span className="text-navy/50">Reconciliations:</span> <span className="font-semibold">{finalizeSummary.reconciliations_applied?.length || 0}</span></div>
                          <div><span className="text-navy/50">Reversals:</span> <span className="font-semibold">{finalizeSummary.reversals_applied?.length || 0}</span></div>
                          <div><span className="text-navy/50">Rows:</span> <span className="font-semibold">{finalizeSummary.totals?.would_insert_rows || 0}</span></div>
                          <div><span className="text-navy/50">Gross:</span> <span className="font-semibold">₹{finalizeSummary.totals?.total_gross_rs || 0}</span></div>
                          <div><span className="text-navy/50">Net:</span> <span className="font-semibold">₹{finalizeSummary.totals?.total_net_rs || 0}</span></div>
                          <div><span className="text-navy/50">Mark Paid:</span> <span className="font-semibold">{finalizeSummary.mark_paid ? "yes" : "no"}</span></div>
                        </div>
                      </div>
                    ) : null}
                    <p className="text-[10px] text-navy/50 leading-relaxed">
                      Reconciliations use the same body as <code>/record-monthly-bonus-reconciliation</code>.
                      Reversals use the same body as <code>/reverse-startup-overcredit</code>.
                      <strong> Always run the dry-run first and copy the output into chat.</strong>
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <RestoreBonusSpPanel />

          {/* Weekly Closing Workflow (UAT / Preview sync) */}
          <div className="bg-sky-50 border border-sky-200 rounded-sm p-5 mb-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 grid place-items-center bg-sky-100 border border-sky-300 rounded-sm shrink-0">
                <Calculator size={16} className="text-sky-700" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-serif text-lg text-royal">Weekly Closing — UAT Workflow</div>
                <p className="text-xs text-navy/60 mt-1 leading-relaxed">
                  Export prod state → import into preview → inject SP purchases → simulate closing.
                  Publish only after customer sign-off. All actions are audit-logged. Run <strong>Export</strong>
                  on prod, then run <strong>Import</strong> + <strong>Simulate</strong> on preview.
                </p>

                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                  {/* Export */}
                  <div className="bg-white border border-sky-200 rounded-sm p-3">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-sky-700 mb-2">Step 1 · Prod</div>
                    <button
                      onClick={exportFullState}
                      disabled={exporting}
                      data-testid="sa-export-full-state"
                      className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-sky-600 hover:bg-sky-700 text-white text-xs font-medium rounded-sm disabled:opacity-50"
                    >
                      {exporting ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                      {exporting ? "Exporting…" : "Export Full State"}
                    </button>
                    <p className="text-[10px] text-navy/50 mt-2 leading-relaxed">
                      Downloads all members, orders, wallets, incomes, closings, KYC, audit log as one JSON file.
                    </p>
                    <div className="mt-3 pt-3 border-t border-sky-100">
                      <button
                        onClick={exportClosingSnapshotCsv}
                        disabled={exportingCsv}
                        data-testid="sa-export-closing-csv"
                        className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded-sm disabled:opacity-50"
                      >
                        {exportingCsv ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />}
                        {exportingCsv ? "Building CSV…" : "Download Current Closing CSV"}
                      </button>
                      <p className="text-[10px] text-navy/50 mt-2 leading-relaxed">
                        Live snapshot — mirrors Account Info "Pending next payout" for every distributor.
                        Inactive members get zero projection. Pure read.
                      </p>
                      {csvSummary ? (
                        <div className="mt-2 text-[10px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-sm px-2 py-1.5" data-testid="sa-csv-summary">
                          <div className="font-mono">{csvSummary.filename}</div>
                          <div className="opacity-80">{csvSummary.total} rows · <strong>{csvSummary.payable} payable</strong> · {csvSummary.downloadedAt}</div>
                        </div>
                      ) : null}
                    </div>
                    <div className="mt-3 pt-3 border-t border-sky-100">
                      <button
                        onClick={recordMonthlyBonusReconciliation}
                        disabled={reconciling}
                        data-testid="sa-record-mbonus-reconcile"
                        className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium rounded-sm disabled:opacity-50"
                      >
                        {reconciling ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />}
                        {reconciling ? "Reconciling…" : "Record Monthly Bonus (Reconciliation)"}
                      </button>
                      <p className="text-[10px] text-navy/50 mt-2 leading-relaxed">
                        Evidence-based record of a missed monthly_bonus credit as a formal ledger row. Default = ledger-only (scalar already carries the ₹). Audit-logged. Idempotent.
                      </p>
                      {reconcileSummary ? (
                        <div className="mt-2 text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5" data-testid="sa-reconcile-summary">
                          <div className="font-mono">{reconcileSummary.user_code} · Tier {reconcileSummary.tier} · {reconcileSummary.period}</div>
                          <div className="opacity-80">₹{reconcileSummary.bonus_rs} · consumed {reconcileSummary.consumed_left_sp}L/{reconcileSummary.consumed_right_sp}R · credit_scalar={String(reconcileSummary.credit_scalar)}</div>
                          <div className="opacity-70 italic">{reconcileSummary.note}</div>
                        </div>
                      ) : null}
                    </div>

                    {/* v37 rev 8.5i — Startup over-credit reversal */}
                    <div className="mt-3 pt-3 border-t border-sky-100">
                      <button
                        onClick={reverseStartupOvercredit}
                        disabled={reversingStartup}
                        data-testid="sa-reverse-startup-overcredit"
                        className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-red-700 hover:bg-red-800 text-white text-xs font-medium rounded-sm disabled:opacity-50"
                      >
                        {reversingStartup ? <Loader2 size={12} className="animate-spin" /> : <Wrench size={12} />}
                        {reversingStartup ? "Reversing…" : "Reverse Startup Over-Credit"}
                      </button>
                      <p className="text-[10px] text-navy/50 mt-2 leading-relaxed">
                        One-time wallet debit for soft startup over-credits (20:20 + 30:30 slabs that fired post 50:50 pair match). Preserves original <code>incomes</code> rows. Idempotent per (user, day).
                      </p>
                      {reverseStartupSummary ? (
                        <div className="mt-2 text-[10px] text-red-900 bg-red-50 border border-red-200 rounded-sm px-2 py-1.5" data-testid="sa-reverse-startup-summary">
                          <div className="font-mono">{reverseStartupSummary.user_code} · −₹{reverseStartupSummary.amount_rs}</div>
                          <div className="opacity-80">scalar {reverseStartupSummary.scalar_before_rs} → <strong>{reverseStartupSummary.scalar_after_rs}</strong></div>
                          <div className="opacity-70 italic">ref_id: {reverseStartupSummary.ref_id}</div>
                        </div>
                      ) : null}
                    </div>

                    {/* v37 rev 8.5i — Commit current closing cycle */}
                    <div className="mt-3 pt-3 border-t border-sky-100">
                      <button
                        onClick={commitCurrentClosing}
                        disabled={committingClosing}
                        data-testid="sa-commit-current-closing"
                        className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-indigo-700 hover:bg-indigo-800 text-white text-xs font-medium rounded-sm disabled:opacity-50"
                      >
                        {committingClosing ? <Loader2 size={12} className="animate-spin" /> : <Calculator size={12} />}
                        {committingClosing ? "Committing…" : "Commit Current Closing"}
                      </button>
                      <p className="text-[10px] text-navy/50 mt-2 leading-relaxed">
                        Freezes the current cycle into <code>weekly_closings_ledger</code> rows (status=open) using CSV math. Supports dry-run first. Idempotent per (period, source).
                      </p>
                      {commitClosingSummary ? (
                        <div className="mt-2 text-[10px] text-indigo-900 bg-indigo-50 border border-indigo-200 rounded-sm px-2 py-1.5" data-testid="sa-commit-closing-summary">
                          <div className="font-mono">{commitClosingSummary.period_label}{commitClosingSummary.dry_run ? " · DRY-RUN" : ""}</div>
                          <div className="opacity-80">
                            {commitClosingSummary.dry_run ? commitClosingSummary.would_insert_rows : commitClosingSummary.inserted_rows} rows · ₹{commitClosingSummary.total_gross_rs} gross · <strong>₹{commitClosingSummary.total_net_rs}</strong> net
                          </div>
                          <div className="opacity-70 italic">source: {commitClosingSummary.source}</div>
                        </div>
                      ) : null}
                    </div>

                    {/* v37 rev 8.5j — Amend open closing row */}
                    <div className="mt-3 pt-3 border-t border-sky-100">
                      <button
                        onClick={amendOpenClosingRow}
                        disabled={amending}
                        data-testid="sa-amend-open-closing-row"
                        className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-orange-600 hover:bg-orange-700 text-white text-xs font-medium rounded-sm disabled:opacity-50"
                      >
                        {amending ? <Loader2 size={12} className="animate-spin" /> : <Wrench size={12} />}
                        {amending ? "Amending…" : "Amend Open Closing Row"}
                      </button>
                      <p className="text-[10px] text-navy/50 mt-2 leading-relaxed">
                        Surgical update to a single <code>weekly_closings_ledger</code> row (status=open only). Use when a reconciliation ran AFTER the commit (e.g. Vishal's Sept Tier-1 ₹1,500 bonus). Recomputes gross/TDS/net. Audit-logged.
                      </p>
                      {amendSummary ? (
                        <div className="mt-2 text-[10px] text-orange-900 bg-orange-50 border border-orange-200 rounded-sm px-2 py-1.5" data-testid="sa-amend-summary">
                          <div className="font-mono">{amendSummary.user_code} · {amendSummary.period_label}</div>
                          <div className="opacity-80">gross ₹{amendSummary.before.gross_rs} → <strong>₹{amendSummary.after.gross_rs}</strong> · net ₹{amendSummary.before.net_rs} → <strong>₹{amendSummary.after.net_rs}</strong></div>
                        </div>
                      ) : null}
                    </div>

                    {/* v37 rev 8.5i — Mark closing paid */}
                    <div className="mt-3 pt-3 border-t border-sky-100">
                      <button
                        onClick={markClosingPaid}
                        disabled={markingPaid}
                        data-testid="sa-mark-closing-paid"
                        className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-900 text-white text-xs font-medium rounded-sm disabled:opacity-50"
                      >
                        {markingPaid ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                        {markingPaid ? "Marking…" : "Mark Closing Paid"}
                      </button>
                      <p className="text-[10px] text-navy/50 mt-2 leading-relaxed">
                        Flips every ledger row's <code>status</code> to "paid" + stamps <code>paid_at</code> for a given <code>period_label</code>. Call this AFTER your bank transfers reach each IBA.
                      </p>
                      {markPaidSummary ? (
                        <div className="mt-2 text-[10px] text-slate-900 bg-slate-100 border border-slate-300 rounded-sm px-2 py-1.5" data-testid="sa-mark-paid-summary">
                          <div className="font-mono">{markPaidSummary.period_label}</div>
                          <div className="opacity-80">matched {markPaidSummary.matched_rows} · modified <strong>{markPaidSummary.modified_rows}</strong></div>
                          <div className="opacity-70 italic">paid_at: {markPaidSummary.paid_at}</div>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  {/* Import */}
                  <div className="bg-white border border-sky-200 rounded-sm p-3">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-rose mb-2">Step 2 · Preview only</div>
                    <label className={`w-full inline-flex items-center justify-center gap-2 px-3 py-2 border text-xs font-medium rounded-sm cursor-pointer ${importing ? "opacity-50 pointer-events-none bg-navy/10 border-navy/20" : "bg-white hover:bg-rose/5 border-rose text-rose"}`}>
                      {importing ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                      {importing ? "Importing…" : "Import JSON"}
                      <input
                        type="file"
                        accept=".json"
                        className="hidden"
                        data-testid="sa-import-full-state"
                        disabled={importing}
                        onChange={(e) => importFullState(e.target.files?.[0])}
                      />
                    </label>
                    <p className="text-[10px] text-navy/50 mt-2 leading-relaxed">
                      Wipes preview + restores from prod's export. <strong className="text-rose">Never run on prod.</strong>
                    </p>
                  </div>

                  {/* Tree repair */}
                  <div className="bg-white border border-sky-200 rounded-sm p-3">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-navy/70 mb-2">Step 2b · One-time on prod</div>
                    <button
                      onClick={runTreeRepair}
                      disabled={repairing}
                      data-testid="sa-run-tree-repair"
                      className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-navy hover:bg-navy/90 text-white text-xs font-medium rounded-sm disabled:opacity-50"
                    >
                      {repairing ? <Loader2 size={12} className="animate-spin" /> : <Wrench size={12} />}
                      {repairing ? "Repairing…" : "Repair Tree Pointers"}
                    </button>
                    <p className="text-[10px] text-navy/50 mt-2 leading-relaxed">
                      Fixes broken left/right_child pointers + parent_code drift. Idempotent.
                    </p>
                  </div>

                  {/* Inject SP */}
                  <div className="bg-white border border-gold/60 rounded-sm p-3">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-gold-deep mb-2">Step 3 · Weekly</div>
                    <label className={`w-full inline-flex items-center justify-center gap-2 px-3 py-2 border text-xs font-medium rounded-sm cursor-pointer ${injecting ? "opacity-50 pointer-events-none bg-navy/10 border-navy/20" : "bg-gold-deep hover:bg-gold-deep/90 border-gold-deep text-white"}`}>
                      {injecting ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />}
                      {injecting ? "Injecting…" : "Inject SP Purchases"}
                      <input
                        type="file"
                        accept=".json"
                        className="hidden"
                        data-testid="sa-inject-sp"
                        disabled={injecting}
                        onChange={(e) => injectSpPurchases(e.target.files?.[0])}
                      />
                    </label>
                    <p className="text-[10px] text-navy/50 mt-2 leading-relaxed">
                      Upload <code className="text-[10px]">{`{purchases:[{user_code,sp}]}`}</code>. Bumps SP + credits instant income + propagates upline.
                    </p>
                  </div>

                  {/* Simulate */}
                  <div className="bg-white border border-sky-200 rounded-sm p-3">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-olive mb-2">Step 4 · Preview</div>
                    <div className="flex gap-2">
                      <button
                        onClick={runClosingSimulate}
                        disabled={simulating}
                        data-testid="sa-run-closing-simulate"
                        className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 bg-olive hover:bg-olive/90 text-white text-xs font-medium rounded-sm disabled:opacity-50"
                      >
                        {simulating ? <Loader2 size={12} className="animate-spin" /> : <Calculator size={12} />}
                        {simulating ? "Running…" : "Simulate"}
                      </button>
                      <button
                        onClick={() => downloadClosingCsv(false)}
                        title="Download full CSV (all IBAs)"
                        data-testid="sa-download-closing-csv"
                        className="inline-flex items-center justify-center px-3 py-2 border border-olive text-olive hover:bg-olive/5 text-xs rounded-sm"
                      >
                        <FileText size={12} />
                      </button>
                    </div>
                    <button
                      onClick={() => downloadClosingCsv(true)}
                      title="Download active-IBA-only weekly payout sheet (share with customer)"
                      data-testid="sa-download-active-csv"
                      className="mt-2 w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-navy hover:bg-navy/90 text-white text-xs font-medium rounded-sm"
                    >
                      <Download size={12} />
                      Active-only Weekly Payout CSV
                    </button>
                    <p className="text-[10px] text-navy/50 mt-2 leading-relaxed">
                      Read-only projection of pair, monthly bonus + rank rewards for every IBA.
                    </p>
                  </div>
                </div>

                {/* Rule fixes row — one-off maintenance buttons */}
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white border border-rose/40 rounded-sm p-3">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-rose mb-2">Rule Fix · One-off</div>
                    <button
                      onClick={applyNewRulesCleanup}
                      disabled={cleaningRules}
                      data-testid="sa-apply-new-rules-cleanup"
                      className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-rose hover:bg-rose/90 text-white text-xs font-medium rounded-sm disabled:opacity-50"
                    >
                      {cleaningRules ? <Loader2 size={12} className="animate-spin" /> : <Wrench size={12} />}
                      {cleaningRules ? "Applying…" : "Apply New Commission Rules"}
                    </button>
                    <p className="text-[10px] text-navy/50 mt-2 leading-relaxed">
                      Removes self-retail on own package + single-leg booster + admin income. Run once per env after deploy.
                    </p>
                    {cleanupSummary && (
                      <div className="mt-2 text-[10px] font-mono bg-rose/5 border border-rose/20 rounded-sm p-2 text-navy/70">
                        <div className="text-rose font-semibold">
                          {cleanupSummary.incomes_deleted} incomes · ₹{(cleanupSummary.wallet_debits_total_rs || 0).toLocaleString("en-IN")} reversed
                        </div>
                        <div>self_retail: {cleanupSummary.buckets.self_retail_rows} rows · ₹{cleanupSummary.buckets.self_retail_amount}</div>
                        <div>single-leg booster: {cleanupSummary.buckets.booster_single_leg_rows} rows · ₹{cleanupSummary.buckets.booster_single_leg_amount}</div>
                        <div>admin income: {cleanupSummary.buckets.admin_income_rows} rows · ₹{cleanupSummary.buckets.admin_income_amount}</div>
                      </div>
                    )}
                  </div>
                  <div className="bg-white border border-royal/40 rounded-sm p-3">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-royal mb-2">Wallet Recompute · One-off</div>
                    <button
                      onClick={recomputeWallets}
                      disabled={recomputing}
                      data-testid="sa-recompute-wallets"
                      className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-royal hover:bg-royal/90 text-white text-xs font-medium rounded-sm disabled:opacity-50"
                    >
                      {recomputing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                      {recomputing ? "Recomputing…" : "Recompute Wallets from Ledger"}
                    </button>
                    <p className="text-[10px] text-navy/50 mt-2 leading-relaxed">
                      wallet.income = Σ incomes − Σ debits. Also heals null-direction wallet_txns. Idempotent.
                    </p>
                    {recomputeSummary && (
                      <div className="mt-2 text-[10px] font-mono bg-royal/5 border border-royal/20 rounded-sm p-2 text-navy/70 max-h-40 overflow-y-auto">
                        <div className="text-royal font-semibold sticky top-0 bg-royal/5">
                          {recomputeSummary.users_adjusted} adjusted · {recomputeSummary.wallet_txns_direction_healed} txns healed
                        </div>
                        {(recomputeSummary.adjustments || []).slice(0, 8).map((a) => (
                          <div key={a.user_code}>
                            {a.user_code}: ₹{a.was_rs} → ₹{a.now_rs} <span className={a.delta_rs > 0 ? "text-olive" : "text-rose"}>({a.delta_rs > 0 ? "+" : ""}{a.delta_rs})</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Sync wallets to projected weekly payout — customer rule 18 Feb 2026 */}
                <div className="mt-4 bg-white border border-olive/40 rounded-sm p-3">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-olive mb-2">Sync Wallets · Weekly Payout</div>
                  <button
                    onClick={syncWalletsToPayout}
                    disabled={syncingWallets}
                    data-testid="sa-sync-wallets-to-payout"
                    className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-olive hover:bg-olive/90 text-white text-xs font-semibold rounded-sm disabled:opacity-50"
                  >
                    {syncingWallets ? <Loader2 size={12} className="animate-spin" /> : <Calculator size={12} />}
                    {syncingWallets ? "Syncing…" : "Set Every IBA Wallet = Projected Weekly Payout"}
                  </button>
                  <p className="text-[10px] text-navy/50 mt-2 leading-relaxed">
                    <strong>wallet.income = pair + direct_ref + monthly_bonus + rank_reward</strong> for every distributor/franchise.
                    Javed ₹9,500 · Sunil ₹500 · everyone matches Simulate exactly. Startup / booster stay in the incomes ledger but are excluded from wallet.income.
                  </p>
                  {syncWalletsSummary && (
                    <div className="mt-2 text-[10px] font-mono bg-olive/5 border border-olive/20 rounded-sm p-2 text-navy/70 max-h-52 overflow-y-auto">
                      <div className="text-olive font-semibold sticky top-0 bg-olive/5">
                        {syncWalletsSummary.users_synced} synced · total wallet.income = ₹{Number(syncWalletsSummary.total_wallet_income_rs || 0).toLocaleString("en-IN")}
                      </div>
                      {(syncWalletsSummary.adjustments || []).slice(0, 12).map((a) => (
                        <div key={a.user_code}>
                          {a.user_code} {a.name?.slice(0, 20)}: ₹{a.was_rs} → ₹{a.now_rs} <span className={a.delta_rs > 0 ? "text-olive" : "text-rose"}>({a.delta_rs > 0 ? "+" : ""}{a.delta_rs})</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Commit weekly closing — irreversible (customer flow 18 Feb 2026) */}
                <div className="mt-4 bg-white border-2 border-rose/60 rounded-sm p-3">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-rose mb-2">Weekly Closing · IRREVERSIBLE</div>
                  <button
                    onClick={commitWeeklyClosing}
                    disabled={committing}
                    data-testid="sa-commit-weekly-closing"
                    className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-rose hover:bg-rose/90 text-white text-xs font-bold rounded-sm disabled:opacity-50"
                  >
                    {committing ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />}
                    {committing ? "Committing…" : "Commit Weekly Closing (book payout in ledger)"}
                  </button>
                  <p className="text-[10px] text-navy/50 mt-2 leading-relaxed">
                    Books current projected payout as REAL incomes + wallet_txns per user, then decrements SP legs + monthly SP + zeros wallet.income.
                    Idempotent per <code>period_label</code> · IRREVERSIBLE · run ONCE per closing week (e.g. <code>05_sep_to_12_sep_2026</code>).
                  </p>
                  {commitSummary && (
                    <div className="mt-2 text-[10px] font-mono bg-rose/5 border border-rose/20 rounded-sm p-2 text-navy/70 max-h-52 overflow-y-auto">
                      <div className="text-rose font-semibold sticky top-0 bg-rose/5">
                        {commitSummary.users_committed} committed · total gross ₹{Number(commitSummary.total_gross_rs || 0).toLocaleString("en-IN")} · period {commitSummary.period_label}
                      </div>
                      {(commitSummary.rows || []).slice(0, 12).map((r) => (
                        <div key={r.user_code}>
                          {r.user_code} {(r.name || "").slice(0, 20)}: ₹{r.gross_rs} · matched {r.matched_sp} SP · L {r.left_before}→{r.left_after} · R {r.right_before}→{r.right_after}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Fix transaction descriptions — one-off (customer request 18 Feb 2026) */}
                <div className="mt-4 bg-white border border-royal/40 rounded-sm p-3">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-royal mb-2">Fix Transaction Wording · One-off</div>
                  <button
                    onClick={fixTransactionDescriptions}
                    disabled={fixingDescs}
                    data-testid="sa-fix-tx-descriptions"
                    className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-royal hover:bg-royal/90 text-white text-xs font-semibold rounded-sm disabled:opacity-50"
                  >
                    {fixingDescs ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                    {fixingDescs ? "Rewriting…" : "Rewrite Booster + Expired-Referral Wording"}
                  </button>
                  <p className="text-[10px] text-navy/50 mt-2 leading-relaxed">
                    <strong>Booster:</strong> "(no matching required)" → "(both legs qualified)".
                    <strong> Direct Ref EXPIRED:</strong> friendlier 30-day-window phrasing. Idempotent, no amount change.
                  </p>
                  {fixDescsSummary && (
                    <div className="mt-2 text-[10px] font-mono bg-royal/5 border border-royal/20 rounded-sm p-2 text-navy/70">
                      <div className="text-royal font-semibold">
                        {fixDescsSummary.total_rows_rewritten} rows rewritten
                      </div>
                      <div>booster incomes: {fixDescsSummary.counts.booster_incomes}</div>
                      <div>booster wallet_txns: {fixDescsSummary.counts.booster_wallet_txns}</div>
                      <div>expired incomes: {fixDescsSummary.counts.expired_incomes}</div>
                      <div>tail softened: {fixDescsSummary.counts.expired_softened}</div>
                    </div>
                  )}
                </div>

                {/* Export summary */}
                {exportSummary && (
                  <div className="mt-4 text-[11px] font-mono bg-white border border-sky-200 rounded-sm p-3">
                    <div className="text-olive font-semibold mb-1">
                      Exported {exportSummary.total.toLocaleString()} records at {exportSummary.downloadedAt}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-0.5 text-navy/70">
                      {Object.entries(exportSummary.counts).map(([k, v]) => (
                        <div key={k}><span className="text-navy/50">{k}:</span> <span className="font-semibold">{v}</span></div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Import summary */}
                {importSummary && (
                  <div className="mt-4 text-[11px] font-mono bg-white border border-sky-200 rounded-sm p-3">
                    <div className="text-olive font-semibold mb-1">
                      Imported {importSummary.totalInserted.toLocaleString()} records at {importSummary.importedAt}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-0.5 text-navy/70">
                      {Object.entries(importSummary.inserted).map(([k, v]) => (
                        <div key={k}>
                          <span className="text-navy/50">{k}:</span>{" "}
                          <span className="font-semibold">{v}</span>
                          {importSummary.wiped[k] > 0 && (
                            <span className="text-rose"> (wiped {importSummary.wiped[k]})</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Inject summary */}
                {injectSummary && (
                  <div className="mt-4 text-[11px] font-mono bg-white border border-gold/60 rounded-sm p-3">
                    <div className="text-gold-deep font-semibold mb-1">
                      Injected {injectSummary.successful}/{injectSummary.total} SP purchases at {injectSummary.injectedAt}
                    </div>
                    <div className="overflow-x-auto max-h-64 overflow-y-auto">
                      <table className="w-full text-[10px]">
                        <thead className="text-navy/50 sticky top-0 bg-white">
                          <tr>
                            <th className="text-left py-0.5">Code</th>
                            <th className="text-left">Name</th>
                            <th className="text-right">SP</th>
                            <th className="text-right">Self SP</th>
                            <th className="text-center">Activated</th>
                            <th className="text-right">Retail ₹</th>
                          </tr>
                        </thead>
                        <tbody>
                          {injectSummary.results.map((r) => (
                            <tr key={r.user_code} className="border-t border-navy/5">
                              <td className="font-mono">{r.user_code}</td>
                              <td className="truncate max-w-[160px]">{r.name}</td>
                              <td className="text-right">{r.sp}</td>
                              <td className="text-right">{r.new_self_sp}</td>
                              <td className={`text-center ${r.activated_now ? "text-olive font-semibold" : "text-navy/40"}`}>
                                {r.activated_now ? "YES" : "—"}
                              </td>
                              <td className="text-right">₹{(r.self_retail_paid_rs || 0).toLocaleString("en-IN")}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Repair summary */}
                {repairSummary && (
                  <div className="mt-4 text-[11px] font-mono bg-white border border-navy/30 rounded-sm p-3">
                    <div className="text-navy font-semibold mb-1">
                      Tree repair complete
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-0.5 text-navy/70">
                      <div><span className="text-navy/50">parent_code fixes:</span> <span className="font-semibold">{repairSummary.parent_code_fixes}</span></div>
                      <div><span className="text-navy/50">Pointers rebuilt:</span> <span className="font-semibold">{repairSummary.repaired_pointers}</span></div>
                      <div><span className="text-navy/50">Parents touched:</span> <span className="font-semibold">{repairSummary.parents_touched}</span></div>
                    </div>
                    {repairSummary.parent_code_fix_details?.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-dashed border-navy/10">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-navy/50 mb-1">Mis-placements corrected</div>
                        {repairSummary.parent_code_fix_details.map((f) => (
                          <div key={f.user_code} className="text-[10px]">
                            <span className="font-mono">{f.user_code}</span>: parent {f.old_parent_code} → <span className="text-olive">{f.new_parent_code}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Simulate summary */}
                {simulateResult && (
                  <div className="mt-4 text-[11px] font-mono bg-white border border-sky-200 rounded-sm p-3">
                    <div className="text-olive font-semibold mb-2">
                      Closing simulate at {new Date(simulateResult.generated_at).toLocaleString()}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-0.5 text-navy/70 mb-3">
                      <div><span className="text-navy/50">Users processed:</span> <span className="font-semibold">{simulateResult.grand_totals.users_processed}</span></div>
                      <div><span className="text-navy/50">Active:</span> <span className="font-semibold">{simulateResult.grand_totals.active_users}</span></div>
                      <div><span className="text-navy/50">Total L SP:</span> <span className="font-semibold">{simulateResult.grand_totals.total_left_sp.toLocaleString()}</span></div>
                      <div><span className="text-navy/50">Total R SP:</span> <span className="font-semibold">{simulateResult.grand_totals.total_right_sp.toLocaleString()}</span></div>
                      <div><span className="text-navy/50">Matched:</span> <span className="font-semibold">{simulateResult.grand_totals.projected_matched_sp.toLocaleString()}</span></div>
                      <div><span className="text-navy/50">Carry:</span> <span className="font-semibold">{simulateResult.grand_totals.projected_carry_sp.toLocaleString()}</span></div>
                      <div><span className="text-navy/50">Capped IBAs:</span> <span className="font-semibold text-rose">{simulateResult.grand_totals.capped_users}</span></div>
                      <div><span className="text-navy/50">Rank upgrades:</span> <span className="font-semibold text-olive">{simulateResult.grand_totals.rank_upgrades}</span></div>
                    </div>
                    <div className="border-t border-dashed border-navy/10 pt-2 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-0.5 text-navy/70">
                      <div><span className="text-navy/50">Pair:</span> <span className="font-semibold">₹{simulateResult.grand_totals.projected_pair_income_rs.toLocaleString("en-IN")}</span></div>
                      <div><span className="text-navy/50">Direct-ref:</span> <span className="font-semibold">₹{simulateResult.grand_totals.projected_direct_ref_income_rs.toLocaleString("en-IN")}</span></div>
                      <div><span className="text-navy/50">Monthly Bonus:</span> <span className="font-semibold">₹{simulateResult.grand_totals.projected_monthly_bonus_rs.toLocaleString("en-IN")}</span></div>
                      <div><span className="text-navy/50">Rank Reward:</span> <span className="font-semibold">₹{simulateResult.grand_totals.projected_rank_reward_rs.toLocaleString("en-IN")}</span></div>
                    </div>
                    <div className="mt-2 pt-2 border-t border-dashed border-navy/10 text-royal">
                      <span className="text-navy/50 uppercase tracking-wider text-[10px]">Weekly payout (paid at closing):</span>{" "}
                      <span className="font-serif text-lg font-bold" data-testid="sa-sim-grand-total">
                        ₹{(simulateResult.grand_totals.projected_weekly_payout_rs || 0).toLocaleString("en-IN")}
                      </span>
                    </div>
                    {simulateResult.top10_near_cap.length > 0 && (
                      <div className="mt-3 pt-2 border-t border-dashed border-navy/10">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-navy/50 mb-1">Top 10 near cap · rank progress</div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-[10px]">
                            <thead className="text-navy/50">
                              <tr>
                                <th className="text-left py-0.5">User</th>
                                <th className="text-left">Name</th>
                                <th className="text-right">L SP</th>
                                <th className="text-right">R SP</th>
                                <th className="text-right">Pairs (now → proj.)</th>
                                <th className="text-left pl-2">Rank (now → proj.)</th>
                                <th className="text-left pl-2">Next</th>
                                <th className="text-right">Pair ₹</th>
                                <th className="text-right">Cap %</th>
                                <th className="text-right">Payout ₹</th>
                              </tr>
                            </thead>
                            <tbody>
                              {simulateResult.top10_near_cap.map((r) => (
                                <tr key={r.user_code} className="border-t border-navy/5" data-testid={`sa-sim-row-${r.user_code}`}>
                                  <td className="py-0.5 font-mono">{r.user_code}</td>
                                  <td className="truncate max-w-[140px]">{r.name}</td>
                                  <td className="text-right">{r.left_sp.toLocaleString()}</td>
                                  <td className="text-right">{r.right_sp.toLocaleString()}</td>
                                  <td className="text-right">
                                    {r.current_pairs}
                                    {r.projected_pairs !== r.current_pairs && (
                                      <span className="text-olive"> → {r.projected_pairs}</span>
                                    )}
                                  </td>
                                  <td className="pl-2">
                                    <span className={r.current_rank === "—" ? "text-navy/40" : "font-semibold text-royal"}>
                                      {r.current_rank}
                                    </span>
                                    {r.projected_rank !== r.current_rank && (
                                      <span className="text-olive"> → {r.projected_rank}</span>
                                    )}
                                  </td>
                                  <td className="pl-2 text-navy/50">
                                    {r.next_rank}
                                    {r.pairs_to_next_rank > 0 && (
                                      <span className="text-gold-deep"> (+{r.pairs_to_next_rank})</span>
                                    )}
                                  </td>
                                  <td className="text-right">₹{r.pair_income_rs.toLocaleString("en-IN")}</td>
                                  <td className={`text-right ${r.capped ? "text-rose font-semibold" : ""}`}>
                                    {r.weekly_pct_cap_used}%
                                  </td>
                                  <td className="text-right font-semibold">₹{(r.weekly_payout_rs || 0).toLocaleString("en-IN")}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Emergency: Catalogue rebuild — reflects preview on production */}
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-5 mb-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 grid place-items-center bg-amber-100 border border-amber-300 rounded-sm shrink-0">
                <RefreshCw size={16} className="text-amber-700" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-serif text-lg text-royal">Rebuild Catalogue (33 SKUs)</div>
                <p className="text-xs text-navy/60 mt-1 leading-relaxed">
                  Force-writes the canonical 33-SKU catalogue (names, prices, categories, images)
                  into this environment's database. Use this on production if products are missing
                  or images are wrong. <strong>Existing admin-uploaded images are preserved</strong>
                  when the underlying file is still on disk. Safe to run multiple times.
                </p>
                <button
                  onClick={rebuildCatalogue}
                  disabled={rebuilding}
                  data-testid="sa-rebuild-catalogue"
                  className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium rounded-sm disabled:opacity-50"
                >
                  {rebuilding ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                  {rebuilding ? "Rebuilding…" : "Rebuild Now"}
                </button>
                {rebuildReport && (
                  <div className="mt-3 text-[11px] font-mono bg-white border border-amber-200 rounded-sm p-3">
                    <div><span className="text-olive font-semibold">Inserted ({rebuildReport.inserted_count}):</span> {rebuildReport.inserted.join(", ") || "—"}</div>
                    <div className="mt-1"><span className="text-royal font-semibold">Updated ({rebuildReport.updated_count}):</span> {rebuildReport.updated.length} SKUs refreshed</div>
                    {rebuildReport.missing_file_count > 0 && (
                      <div className="mt-1 text-rose">
                        <span className="font-semibold">Missing files on disk ({rebuildReport.missing_file_count}):</span>{" "}
                        {rebuildReport.missing_files.map((m) => m.sku).join(", ")}
                        <div className="text-navy/50 mt-0.5">DB image not overwritten for these — file needs to exist first.</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="text-xs text-navy/50 leading-relaxed">
            <strong className="text-navy/70">Note:</strong> Superadmin has full admin access. Use
            the regular <Link to="/admin" className="text-gold-deep hover:text-royal underline">Admin dashboard</Link>{" "}
            for members, KYC, orders, payouts, notices, reports and audit logs. This panel adds the
            Home Banner tool and catalogue rebuild that only superadmin can access.
          </div>
        </div>
      </main>
    </div>
  );
}
